import { fromArrayBuffer } from 'geotiff';
import type { PointCloudData } from '../../types';

const MAX_DEM_POINTS = 500_000;

/**
 * Parse a GeoTIFF DEM file (ArrayBuffer) into PointCloudData.
 *
 * Reads the first image band as elevation, converts the 2D raster grid
 * into a point cloud with x = col, y = row, z = elevation.
 * Large rasters are subsampled to ~500 k points.
 */
export async function parseDem(arrayBuffer: ArrayBuffer): Promise<PointCloudData> {
  const tiff = await fromArrayBuffer(arrayBuffer);
  const image = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();

  const raster = await image.readRasters({ interleave: false });
  // raster is an array of TypedArrays, one per sample
  const elevationBand = raster[0] as Float32Array | Int16Array | Uint16Array | Int32Array | Float64Array;

  const totalPixels = width * height;

  // Determine subsample step to cap point count
  const step = Math.max(1, Math.ceil(Math.sqrt(totalPixels / MAX_DEM_POINTS)));
  const cols = Math.ceil(width / step);
  const rows = Math.ceil(height / step);
  const pointCount = cols * rows;

  const positions = new Float32Array(pointCount * 3);
  const colors = new Float32Array(pointCount * 3);
  const intensities = new Float32Array(pointCount);
  const classifications = new Uint8Array(pointCount);

  // Find min/max elevation for normalization
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < totalPixels; i++) {
    const z = elevationBand[i];
    if (Number.isNaN(z)) continue;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  if (!isFinite(minZ)) minZ = 0;
  if (!isFinite(maxZ)) maxZ = 1;
  const rangeZ = maxZ - minZ || 1;

  let idx = 0;
  for (let row = 0; row < height; row += step) {
    for (let col = 0; col < width; col += step) {
      const pixelIdx = row * width + col;
      const z = elevationBand[pixelIdx];

      // Skip no-data (NaN or very large sentinel values)
      if (Number.isNaN(z) || z === image.getGDALNoData() || z < -9999) {
        // Use minZ as fallback so the point still exists
        const fallbackZ = minZ;
        positions[idx * 3] = col;
        positions[idx * 3 + 1] = row;
        positions[idx * 3 + 2] = fallbackZ;
        const t = 0;
        colors[idx * 3] = t;
        colors[idx * 3 + 1] = 0.5;
        colors[idx * 3 + 2] = 1.0 - t;
        intensities[idx] = 0;
      } else {
        positions[idx * 3] = col;
        positions[idx * 3 + 1] = row;
        positions[idx * 3 + 2] = z;
        const t = (z - minZ) / rangeZ;
        // Elevation color map: blue (low) → green → red (high)
        colors[idx * 3] = t;
        colors[idx * 3 + 1] = 0.5;
        colors[idx * 3 + 2] = 1.0 - t;
        intensities[idx] = t;
      }
      idx++;
    }
  }

  // Actual point count after filtering (should equal cols * rows)
  const actualCount = idx;

  return {
    positions: positions.slice(0, actualCount * 3),
    colors: colors.slice(0, actualCount * 3),
    intensities: intensities.slice(0, actualCount),
    classifications: classifications.slice(0, actualCount),
    boundingBox: {
      min: { x: 0, y: 0, z: minZ },
      max: { x: width, y: height, z: maxZ },
    },
    pointCount: actualCount,
  };
}
